import React from 'react';
import {
  DEFAULT_MUSIC_PREFS,
  normalizeRhythmPattern,
  normalizeTokenArray,
  VexRhythmStrip,
  type MusicGenerationPrefs,
  type RhythmicCell,
  type RhythmicPatternPayload,
  type MusicalToken,
  type ScoreRenderEvent,
  type TimeSignature,
} from '@interactive-room';

interface RhythmPatternStripProps {
  rhythmPattern?: RhythmicPatternPayload;
  patternTokens?: string[];
  beatCells?: RhythmicCell[];
  rhythmicCells?: RhythmicCell[];
  timeSignature?: TimeSignature;
  meter?: string;
  rhythmMethod?: MusicGenerationPrefs['readingMethod'];
  graphicMode?: MusicGenerationPrefs['graphicMode'];
  showSlots?: boolean;
  slotMode?: 'order' | 'missing';
  printMode?: boolean;
  targetBarNumber?: number;
  dottedIndices?: number[];
  ties?: [number, number][];
  keySignature?: string;
  showClef?: boolean;
  scoreEvents?: ScoreRenderEvent[];
}

function resolveTimeSignature(
  rhythmPattern?: RhythmicPatternPayload,
  timeSignature?: string,
  meter?: string,
): TimeSignature {
  const fromPattern = rhythmPattern?.timeSignature;
  if (fromPattern && ['4/4', '3/4', '2/4'].includes(fromPattern)) return fromPattern;
  const raw = String(timeSignature || meter || '').trim();
  if (['4/4', '3/4', '2/4'].includes(raw)) return raw as TimeSignature;
  return '4/4';
}

function resolvePattern(props: RhythmPatternStripProps) {
  const fromRhythm = normalizeRhythmPattern(props.rhythmPattern);
  if (fromRhythm?.pattern?.length) {
    return {
      ...fromRhythm,
      timeSignature: resolveTimeSignature(fromRhythm, props.timeSignature, props.meter),
    };
  }
  const tokens = normalizeTokenArray(props.patternTokens);
  if (!tokens.length) return null;
  return {
    timeSignature: resolveTimeSignature(undefined, props.timeSignature, props.meter),
    pattern: tokens,
    missingIndex: fromRhythm?.missingIndex,
  };
}

export function RhythmPatternStrip({
  rhythmPattern,
  patternTokens,
  beatCells,
  rhythmicCells,
  timeSignature,
  meter,
  rhythmMethod,
  graphicMode,
  showSlots = false,
  slotMode = 'order',
  printMode = true,
  targetBarNumber,
  dottedIndices = [],
  ties = [],
  keySignature,
  showClef = true,
  scoreEvents,
}: RhythmPatternStripProps) {
  const resolved = resolvePattern({ rhythmPattern, patternTokens, timeSignature, meter });
  const cells = beatCells?.length ? beatCells : rhythmicCells;
  if (!resolved?.pattern?.length && !cells?.length) {
    return (
      <p className="print-rhythm-pattern__empty text-xs text-[var(--sala-ink-dim)]">
        Patrón rítmico no disponible — revisa timeSignature y patternTokens del bloque.
      </p>
    );
  }

  const prefs: MusicGenerationPrefs = {
    readingMethod: rhythmMethod ?? DEFAULT_MUSIC_PREFS.readingMethod,
    graphicMode: graphicMode ?? DEFAULT_MUSIC_PREFS.graphicMode,
  };

  const missingIndex = slotMode === 'missing' ? resolved?.missingIndex : undefined;
  const pattern = (resolved?.pattern ?? []) as MusicalToken[];
  const ts = resolved?.timeSignature ?? resolveTimeSignature(undefined, timeSignature, meter);

  const meterLabel = targetBarNumber
    ? `Compás ${targetBarNumber} (${ts})`
    : `Compás ${ts}`;

  return (
    <div className={`print-rhythm-pattern${printMode ? ' print-rhythm-pattern--vex' : ''}`}>
      <p className="print-rhythm-pattern__meter">{meterLabel}</p>
      <VexRhythmStrip
        pattern={
          scoreEvents?.length
            ? (scoreEvents.map((e) => e.token) as MusicalToken[])
            : pattern.length
              ? pattern
              : (cells ?? []).flatMap((cell) => cell.tokens)
        }
        beatCells={scoreEvents?.length ? undefined : cells}
        scoreEvents={scoreEvents}
        timeSignature={ts}
        missingIndex={missingIndex}
        showLabels={prefs.graphicMode === 'symbols-labels'}
        readingMethod={prefs.readingMethod}
        printMode={printMode}
        hideMeterLabel
        targetBarNumber={targetBarNumber}
        dottedIndices={dottedIndices}
        ties={ties}
        keySignature={keySignature}
        showClef={showClef}
      />
      {showSlots && (
        <p className="print-rhythm-pattern__slots-hint" aria-hidden>
          Escribe el orden en los casilleros de la hoja.
        </p>
      )}
    </div>
  );
}
